import { useEffect } from 'react';

/**
 * SEO Component - Updates document head with page-specific meta tags
 * @param {string} title - Page title
 * @param {string} description - Page description (max 160 chars)
 * @param {string} keywords - Comma-separated keywords
 * @param {string} canonical - Canonical URL for the page
 * @param {string} image - OG image URL
 * @param {object} schema - JSON-LD structured data
 */
export const SEOHead = ({ 
  title, 
  description, 
  keywords = '',
  canonical = '',
  image = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=630&fit=crop',
  schema = null
}) => {
  const siteName = 'Raj Aluminum & Glass Hardware';
  const baseUrl = 'https://aluminum-glass-hub.preview.emergentagent.com';
  
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} | Premium Glass & Aluminium Work in Faridabad`;
  const fullCanonical = canonical ? `${baseUrl}${canonical}` : baseUrl;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Helper to update or create meta tag
    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary meta tags
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('title', fullTitle);

    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:url', fullCanonical, true);
    setMeta('og:image', image, true);

    // Twitter
    setMeta('twitter:title', fullTitle, true);
    setMeta('twitter:description', description, true);
    setMeta('twitter:image', image, true);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullCanonical);

    // Add page-specific schema if provided
    if (schema) {
      // Remove existing page schema
      const existingSchema = document.querySelector('script[data-page-schema]');
      if (existingSchema) existingSchema.remove();

      // Add new schema
      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-page-schema', 'true');
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);
    }

    // Cleanup on unmount
    return () => {
      const pageSchema = document.querySelector('script[data-page-schema]');
      if (pageSchema) pageSchema.remove();
    };
  }, [fullTitle, description, keywords, fullCanonical, image, schema]);

  return null;
};

// Predefined SEO configs for each page
export const SEO_CONFIG = {
  home: {
    title: null, // Uses default
    description: "Faridabad's #1 aluminum & glass experts. Premium toughened glass partitions, shower enclosures, aluminium doors & windows, ACP facades. 15+ years experience. Call: 085128 53955",
    keywords: "aluminum glass faridabad, glass partition faridabad, aluminium fabrication, toughened glass, shower enclosure, ACP facade, front elevation, office partition",
    canonical: '/'
  },
  about: {
    title: 'About Us',
    description: "Learn about Raj Aluminum & Glass Hardware - Faridabad's trusted aluminum and glass experts since 2010. Quality craftsmanship, experienced team, 1000+ happy customers.",
    keywords: "about raj aluminum, aluminum company faridabad, glass work faridabad, trusted contractor",
    canonical: '/about'
  },
  services: {
    title: 'Our Services',
    description: "Professional aluminum & glass services in Faridabad: Toughened glass partitions, shower enclosures, aluminium doors, windows, ACP/HPL facades, glass railings, and more.",
    keywords: "glass partition service, aluminium door installation, ACP facade work, shower enclosure installation, glass railing faridabad",
    canonical: '/services'
  },
  products: {
    title: 'Products & Price List',
    description: "Browse our aluminum & glass products with prices. Toughened glass from ₹220/sq.ft, aluminium partitions from ₹180/sq.ft. Quality products, competitive rates.",
    keywords: "glass price faridabad, aluminium rate, toughened glass price, partition price, shower glass price",
    canonical: '/products'
  },
  gallery: {
    title: 'Project Gallery',
    description: "View our completed aluminum & glass projects in Faridabad. Office partitions, home glass work, shower enclosures, front elevations, and more. See our craftsmanship.",
    keywords: "glass work photos, aluminium project gallery, completed projects faridabad, office partition design",
    canonical: '/gallery'
  },
  reviews: {
    title: 'Customer Reviews',
    description: "Read what our customers say about Raj Aluminum & Glass Hardware. 4.9★ rating on Google with 100+ reviews. Quality work and excellent service.",
    keywords: "raj aluminum reviews, glass work reviews faridabad, customer testimonials, trusted glass contractor",
    canonical: '/reviews'
  },
  contact: {
    title: 'Contact Us',
    description: "Contact Raj Aluminum & Glass Hardware in Faridabad. Call: 085128 53955, WhatsApp available. Visit our showroom near Sector 21C. Free quotation!",
    keywords: "contact aluminum glass faridabad, glass work enquiry, free quote, aluminium contractor contact",
    canonical: '/contact'
  }
};

export default SEOHead;
